import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import {
  Scene,
  ImageContainer,
  Image,
  SceneWrapper,
  ObserverWrapper,
} from "../styles/3D/styles";
import { vertexShader } from "./shaders/vertex";
import { fragmentShader } from "./shaders/fragment";
import { setPositionPlanes, animatePlanes } from "../animations/landing";
import { alignMeshToDom } from "../utils/alignPlaneToDOM";
import LandingSection from "../components/landingSections";
import BodegaLanding from "../components/bodega";
import animations from "../animations/animations";
import THREEJS from "./three";
import LandingHoverVideo from "../components/landingHoverVideo";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import { getDeviceInfo } from "../data/utils";

const ThreeJsScene = ({
  content,
  zoomingAnimation,
  planesAnimating,
  handleSetPlanesAnimating,
  scrollReady,
  lenisRef,
  cameraRef,
  planesRef,
  landingSceneRef,
}) => {
  const [isWebGLAvailable, setIsWebGLAvailable] = useState(true);
  const [labelsReady, setLabelsReady] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeAnimation, setActiveAnimation] = useState(true);
  const [isLastPlane, setIsLastPlane] = useState(false);

  const mountRef = useRef(null);
  const sceneWrapperRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef(null);
  const planesGroupRef = useRef([]);
  const bodegaSectionRef = useRef(null);
  const labelRefs = useRef([]);
  const labelRendererRef = useRef(null);
  const animationFrameRef = useRef(null);
  const planesAnimatingRef = useRef(false);
  const activeAnimationRef = useRef(true);
  const lastWindowWidth = useRef(0);
  const isMobileRef = useRef(false);
  const zoomingAnimationRef = useRef(true);
  const planesControlRef = useRef(null);
  const lastPlaneRef = useRef(null);

  const BASE_DISTANCE = 5;

  const scaleLabels = () => {
    const distance = cameraRef.current.position.z;
    const scale = BASE_DISTANCE / distance;

    labelRefs.current.forEach(label => {
      if (label.element) {
        label.element.style.transform = `scale(${scale})`;
        label.element.style.transformOrigin = "center center";
      }
    });
  };

  const render = () => {
    controlsRef.current.update();

    rendererRef.current.render(sceneRef.current, cameraRef.current);

    if (labelRendererRef.current) {
      labelRendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    if (!isMobileRef.current) {
      scaleLabels();
    }
  };

  const animate = () => {
    animationFrameRef.current = requestAnimationFrame(animate);
    render();
  };

  const resize = () => {
    const canvas = sceneWrapperRef.current;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (lastWindowWidth.current === width) return;

    const aspect = width / height;

    lastWindowWidth.current = width;

    cameraRef.current.aspect = aspect;
    cameraRef.current.updateProjectionMatrix();
    labelRendererRef.current.setSize(width, height);
    rendererRef.current.setSize(width, height);
  };

  const setLastPlane = value => {
    lastPlaneRef.current = value;
    setIsLastPlane(value);
  };

  const isWebGLAvailableCheck = () => {
    try {
      const canvas = document.createElement("canvas");
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
      );
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!isWebGLAvailableCheck()) {
      console.error("WebGL is not supported in this browser");
      setIsWebGLAvailable(false);
      return;
    }

    const canvas = sceneWrapperRef.current;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const { isMobile } = getDeviceInfo();

    isMobileRef.current = isMobile;
    lastWindowWidth.current = width;

    const mm = animations.matchMedia();

    const sectionContent = content.flatMap((section, index) => {
      return {
        key: section.key,
        sectionIndex: index,
        active: section.active,
        sectionBodega: section.sectionBodega,
        mainImage: {
          desktop: section?.mainImage?.[0]?.image?.fluid,
          mobile: section?.mainImage?.[0]?.imageMobile?.fluid,
        },
        heading: section?.heading,
        color: section?.colors || null,
        cursor: section?.cursor?.[0]?.image?.fluid?.src,
        link: section?.link || null,
      };
    });

    const currentMount = mountRef.current;

    sceneRef.current = new THREEJS.Scene();

    cameraRef.current = new THREEJS.PerspectiveCamera(
      75,
      width / height,
      0.1,
      1000
    );

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      animations.set(cameraRef.current.position, { z: 5 });
    });

    mm.add(
      `(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${
        BREAKPOINTS.xxxl - 1
      }px)`,
      () => {
        animations.set(cameraRef.current.position, { z: 50 });
      }
    );

    mm.add(
      `(min-width: ${BREAKPOINTS.xxxl}px) and (max-width: ${
        BREAKPOINTS.xxlm - 1
      }px)`,
      () => {
        animations.set(cameraRef.current.position, { z: 46 });
      }
    );

    mm.add(
      `(min-width: ${BREAKPOINTS.xxlm}px) and (max-width: ${
        BREAKPOINTS.xxxxl - 1
      }px)`,
      () => {
        animations.set(cameraRef.current.position, { z: 48 });
      }
    );

    mm.add(`(min-width: ${BREAKPOINTS.xxxxl}px)`, () => {
      animations.set(cameraRef.current.position, { z: 50 });
    });

    rendererRef.current = new THREEJS.WebGLRenderer({
      antialias: true,
      alpha: true,
    });

    rendererRef.current.setSize(width, height);
    currentMount.appendChild(rendererRef.current.domElement);

    const labelRenderer = new THREEJS.CSS2DRenderer();
    labelRenderer.setSize(width, height);
    labelRenderer.domElement.style.position = "absolute";
    labelRenderer.domElement.style.top = "0";
    labelRendererRef.current = labelRenderer;
    currentMount.appendChild(labelRenderer.domElement);

    controlsRef.current = new THREEJS.OrbitControls(
      cameraRef.current,
      rendererRef.current.domElement
    );

    controlsRef.current.enabled = false;

    const raycaster = new THREEJS.Raycaster();
    let mouse = new THREEJS.Vector2();

    let intersecting = false;
    let hoveredPlane = null;

    const addPlanes = () => {
      const textureLoader = new THREEJS.TextureLoader();
      const planeGroup = new THREEJS.Group();
      planesGroupRef.current = planeGroup;
      sceneRef.current.add(planeGroup);

      sectionContent.forEach((section, index) => {
        if (!section.active) return;

        if (section.sectionBodega) {
          const bodegaContainer = document.createElement("div");
          bodegaContainer.className = "bodega-container";

          if (!isMobileRef.current) {
            bodegaContainer.style.opacity = "0";
          }

          const root = ReactDOM.createRoot(bodegaContainer);
          root.render(<BodegaLanding content={section} />);

          const bodegaLabel = new THREEJS.CSS2DObject(bodegaContainer);
          bodegaLabel.position.set(0, 0, 0);
          bodegaLabel.renderOrder = 999;

          planesGroupRef.current.add(bodegaLabel);
          planesRef.current.push(bodegaLabel);

          bodegaSectionRef.current = bodegaLabel;
        } else {
          const mainImage = isMobileRef.current
            ? section.mainImage.mobile
            : section.mainImage.desktop;

          const texture = textureLoader.load(mainImage.src);
          const aspectRatio = mainImage.aspectRatio;

          let geometry;

          if (isMobileRef.current) {
            const distanceFromCamera = cameraRef.current.position.z;
            const fovRadians = (cameraRef.current.fov * Math.PI) / 180;

            const visibleHeight =
              2 * Math.tan(fovRadians / 2) * distanceFromCamera;
            const visibleWidth = visibleHeight * (width / height);

            const marginFactor = 0.95;
            const planeWidth = visibleWidth * marginFactor;
            const planeHeight = planeWidth / aspectRatio;

            const maxHeight = visibleHeight * marginFactor;
            if (planeHeight > maxHeight) {
              geometry = new THREEJS.PlaneGeometry(
                maxHeight * aspectRatio,
                maxHeight,
                1,
                1
              );
            } else {
              geometry = new THREEJS.PlaneGeometry(
                planeWidth,
                planeHeight,
                1,
                1
              );
            }
          } else {
            geometry = new THREEJS.PlaneGeometry(10, 6, 128, 128);
          }

          const material = new THREEJS.ShaderMaterial({
            uniforms: {
              uTexture: { value: texture },
              uMouse: { value: new THREEJS.Vector2(2.0, 2.0) },
              uHover: { value: 0.0 },
            },
            depthWrite: false,
            transparent: true,
            vertexShader: vertexShader(),
            fragmentShader: fragmentShader(),
          });

          const mesh = new THREEJS.Mesh(geometry, material);
          mesh.userData = {
            index: index,
            cursor: section.cursor,
          };
          mesh.position.set(0, 0, -1);
          mesh.renderOrder = 1;

          if (index !== 0 && !isMobileRef.current) {
            mesh.visible = false;
          }

          planeGroup.add(mesh);
          planesRef.current.push(mesh);

          const htmlContainer = document.createElement("div");
          htmlContainer.className = "label-root";

          const labelInner = document.createElement("div");
          labelInner.className = "label-inner";

          if (!isMobileRef.current) {
            labelInner.style.opacity = "0";
          }

          if (index !== 0) {
            labelInner.style.filter = `blur(3px)`;
          }

          htmlContainer.appendChild(labelInner);

          const labelObject = new THREEJS.CSS2DObject(htmlContainer);
          labelObject.position.set(0, 0, 0);
          labelObject.renderOrder = 999;
          mesh.add(labelObject);

          labelRefs.current.push({
            element: labelInner,
            wrapper: htmlContainer,
            content: section,
          });
        }
      });

      setLabelsReady(true);
    };

    addPlanes();

    setPositionPlanes(planesRef.current, planesGroupRef.current, isMobileRef);

    const raycasterMouse = event => {
      if (isMobileRef.current) return;

      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, cameraRef.current);
      const intersects = raycaster.intersectObjects(planesRef.current);

      if (intersects.length > 0) {
        intersecting = true;
        const intersected = intersects[0];
        const uv = intersected.uv;
        const mat = intersected.object.material;

        hoveredPlane = intersected.object;

        mat.uniforms.uHover.value = 1.0;

        mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
          animations.to(mat.uniforms.uMouse.value, {
            duration: 0.8,
            x: uv.x,
            y: uv.y,
          });
        });
      } else {
        intersecting = false;
      }
    };

    const returnHoveredPlane = () => {
      if (!hoveredPlane || intersecting) return;
      const mat = hoveredPlane.material;
      mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
        animations.to(mat.uniforms.uMouse.value, {
          delay: 1,
          duration: 0.8,
          x: 2,
          y: 2,
          onComplete: () => {
            mat.uniforms.uHover.value = 0.0;
            hoveredPlane = null;
            mountRef.current.style.cursor = "auto";
          },
        });
      });
    };

    const handleMouseMove = e => {
      raycasterMouse(e);

      if (planesAnimatingRef.current) return;

      if (lastPlaneRef.current) {
        const lastCursor = sectionContent.at(-1)?.cursor;
        mountRef.current.style.cursor = lastCursor
          ? `url('${lastCursor}') 10 10, auto`
          : "auto";
        return;
      }

      if (
        hoveredPlane &&
        hoveredPlane.userData.cursor &&
        !zoomingAnimationRef.current
      ) {
        mountRef.current.style.cursor = `url('${hoveredPlane.userData.cursor}') 10 10, auto`;
      } else {
        mountRef.current.style.cursor = "auto";
      }
    };

    const handleMouseLeave = () => {
      returnHoveredPlane();
    };

    animate();
    resize();

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", resize);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      planesRef.current.forEach(mesh => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach(m => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
        sceneRef.current?.remove(mesh);
      });
      planesRef.current = [];

      labelRefs.current.forEach(label => {
        if (label.wrapper && label.wrapper.parentNode) {
          label.wrapper.parentNode.removeChild(label.wrapper);
        }
      });
      labelRefs.current = [];

      if (planesGroupRef.current) {
        sceneRef.current?.remove(planesGroupRef.current);
        planesGroupRef.current = null;
      }

      if (labelRendererRef.current) {
        labelRendererRef.current.dispose?.();
        if (
          labelRendererRef.current.domElement &&
          labelRendererRef.current.domElement.parentNode === currentMount
        ) {
          currentMount.removeChild(labelRendererRef.current.domElement);
        }
        labelRendererRef.current = null;
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (
          rendererRef.current.domElement &&
          currentMount.contains(rendererRef.current.domElement)
        ) {
          currentMount.removeChild(rendererRef.current.domElement);
        }
        rendererRef.current = null;
      }

      if (sceneRef.current) {
        sceneRef.current.clear();
        sceneRef.current = null;
      }
      cameraRef.current = null;
      controlsRef.current = null;
      animationFrameRef.current = null;

      mm.revert();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateIsMobile = () => {
      isMobileRef.current = window.matchMedia(
        `(max-width: ${BREAKPOINTS.sm - 1}px)`
      ).matches;
    };

    updateIsMobile();

    const mediaQuery = window.matchMedia(
      `(max-width: ${BREAKPOINTS.sm - 1}px)`
    );
    mediaQuery.addEventListener("change", updateIsMobile);

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !scrollReady) return;

    const mm = animations.matchMedia();

    console.log("🟢 Scroll ready - ThreeJsScene");

    planesControlRef.current = animatePlanes(
      planesRef.current,
      planesGroupRef.current,
      setHoveredIndex,
      handleSetPlanesAnimating,
      activeAnimationRef,
      setLastPlane,
      isMobileRef,
      lenisRef,
      mm
    );

    return () => {
      planesControlRef.current?.destroy();
      mm.revert();
    };
  }, [scrollReady]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !scrollReady ||
      !planesControlRef.current
    )
      return;

    zoomingAnimationRef.current = zoomingAnimation;

    const ctrl = planesControlRef.current;
    if (!ctrl) return;

    if (isMobileRef.current) {
      ctrl.enable();
      console.log("🔓 Observer enabled because isMobileRef is true");
      return;
    } else {
      if (zoomingAnimation) {
        ctrl.disable();
        console.log("🔒 Observer disabled because zoomingAnimation is true");
      } else {
        ctrl.enable();
        handleSetPlanesAnimating(false);
        console.log("🔓 Observer enabled because zoomingAnimation is false");
      }
    }
  }, [zoomingAnimation, scrollReady]);

  useEffect(() => {
    if (typeof window === "undefined" || !scrollReady) return;

    const updateAlignment = () => {
      if (!isMobileRef.current) {
        alignMeshToDom(
          planesRef.current[0],
          cameraRef.current,
          landingSceneRef.current,
          mountRef.current
        );
      }
    };

    setTimeout(() => {
      updateAlignment();
    }, 500);
  }, [scrollReady]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    planesAnimatingRef.current = planesAnimating;

    if (planesAnimating) {
      mountRef.current.style.cursor = "auto";
    }
  }, [planesAnimating]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    activeAnimationRef.current = activeAnimation;
  }, [activeAnimation]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    lastPlaneRef.current = isLastPlane;
  }, [isLastPlane]);

  return (
    <>
      {isWebGLAvailable ? (
        <SceneWrapper ref={sceneWrapperRef}>
          <ObserverWrapper>
            <LandingHoverVideo
              content={content}
              hoveredIndex={hoveredIndex}
              planesLength={4}
            />
            <Scene ref={mountRef} />
            {labelsReady &&
              labelRefs.current.map((ref, i) => {
                return createPortal(
                  <LandingSection
                    key={i}
                    index={i}
                    content={ref.content}
                    hoveredIndex={hoveredIndex}
                    setHoveredIndex={setHoveredIndex}
                    planesAnimating={planesAnimatingRef}
                    setActiveAnimation={setActiveAnimation}
                  />,
                  ref.element
                );
              })}
          </ObserverWrapper>
        </SceneWrapper>
      ) : (
        <ImageContainer>
          <Image src="src/images/hs-icon.png" />
        </ImageContainer>
      )}
    </>
  );
};

export default ThreeJsScene;
